import { InvalidSchemaError } from '../../../errors'
import type { SchemaSource } from '../types'
import type { AnyZod } from './introspect'

// Every schema slot is OPTIONAL. Declare it and it must be a `z.object` — because that is the only thing the
// runner can do anything with: a container of named fields to validate input against and publish as the
// method's input model.
//
// Without this guard a wrong shape degrades SILENTLY. `params: z.string()` has no `.shape`, so the method
// publishes no params, the console renders a node with no inputs, validation becomes a passthrough, and the
// service deploys clean — the author finds out when the action cannot be called correctly in production.
//
// So the check runs at REGISTRATION time, while the service module is being `require`d. That means it fires
// on the first call the CLI or console makes against the extension, including the `buildServiceDefinition`
// that `flowrunner deploy` performs per service — so a bad schema stops the deploy instead of shipping.

// Zod's single-child wrappers. An object inside one of these cannot serve as a container: the wrapper has no
// `.shape` and no `.partial()`, so every field would be invisible.
const WRAPPERS = new Set(['optional', 'nullable', 'nullish', 'default', 'prefault'])

const typeOf = (value: unknown): string | undefined =>
  (value as AnyZod | undefined)?._zod?.def?.type as string | undefined

// How to name what the author actually passed, in their own vocabulary.
function describe(value: unknown): string {
  const type = typeOf(value)

  if (type) {
    if (WRAPPERS.has(type)) {
      const inner = typeOf((value as { _zod?: { def?: { innerType?: unknown } } })._zod?.def?.innerType)

      return `z.${ inner ?? 'unknown' }().${ type }()`
    }

    return `z.${ type }()`
  }

  if (value === null) {
    return 'null'
  }

  if (Array.isArray(value)) {
    return 'an array'
  }

  if (typeof value === 'object') {
    return 'a plain object'
  }

  return `a ${ typeof value }`
}

// The most useful next step for this particular mistake.
function hint(value: unknown, slot: string, isReturn: boolean): string {
  const type = typeOf(value)

  if (type && WRAPPERS.has(type)) {
    return '; an object container cannot be optional — mark its fields optional instead'
  }

  if (!type && typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return ' — did you forget to wrap it in z.object({ … })?'
  }

  // Every slot may simply be left out, which is usually what an author reaching for a non-object wanted.
  return isReturn ? '' : `; omit \`${ slot }\` entirely if there is nothing to declare`
}

/**
 * Throws unless `scheme` is a `z.object` (including `z.looseObject` / `z.strictObject`). `undefined` passes —
 * an undeclared slot is legal everywhere.
 */
export function assertObjectSchema(scheme: unknown, source: SchemaSource): void {
  if (scheme == null || typeOf(scheme) === 'object') {
    return
  }

  const slot = source.slot ?? 'schema'
  const kind = source.kind ?? 'extension'
  // A slot spelled with parentheses is a function whose RETURN is being checked.
  const isReturn = slot.endsWith('()')
  // The extension itself has no item id to name; everything else does.
  const subject = source.itemId ? `${ kind } "${ source.itemId }": ` : ''
  const received = describe(scheme)

  throw new InvalidSchemaError(
    `[flow-extension:${ source.serviceId }] ${ subject }\`${ slot }\` ${ isReturn ? 'must return' : 'must be' }` +
      ` a z.object({ … }) — received ${ received }${ hint(scheme, slot, isReturn) }`,
    { serviceId: source.serviceId, kind, itemId: source.itemId, slot, received }
  )
}
